import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Filter, Search, User, Download } from 'lucide-react';

const PaystubReportView = () => {
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const from = searchParams.get('from') || 'ready-to-publish';
  const employeeId = searchParams.get('employee'); // Individual employee view
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(employeeId || 'all');

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Helper function to format date-only strings properly 
  const formatDateOnly = (dateString) => {
    if (!dateString) return '';
    // Handle date-only strings (YYYY-MM-DD) without timezone conversion
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      });
    }
    // Handle full datetime strings
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'numeric', 
      day: 'numeric'
    });
  };

  useEffect(() => {
    fetchPaystubReport();
  }, [postId]);

  const fetchPaystubReport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/api/ai-posts/${postId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setPost(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load paystub report');
      console.error('Error fetching paystub report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    // Get the current employee data for PDF generation
    const currentEmployeeData = getSelectedEmployeeData()[0];
    if (currentEmployeeData) {
      const element = document.getElementById('paystub-content');
      if (element) {
        // Format dates for the template
        const formattedStartDate = formatDateOnly(paystubData.start_date);
        const formattedEndDate = formatDateOnly(paystubData.end_date);
        
        // Use browser's print functionality to generate PDF
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
          <html>
            <head>
              <title>Paystub - ${currentEmployeeData.user_name}</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                .header { text-align: center; margin-bottom: 30px; }
                .employee-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
                .metrics { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 15px; margin-bottom: 30px; }
                .metric { text-align: center; padding: 15px; background: #f8f9fa; border-radius: 8px; }
                .breakdown { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
                .section { padding: 20px; background: #f8f9fa; border-radius: 8px; }
                .line-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
                .total { display: flex; justify-content: space-between; padding: 12px 0; border-top: 2px solid #374151; font-weight: bold; }
                .final-pay { text-align: center; padding: 20px; background: #eff6ff; border-radius: 8px; }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>Employee Paystub</h1>
                <p>Pay Period: ${formattedStartDate} to ${formattedEndDate}</p>
              </div>
              
              <div class="employee-info">
                <div>
                  <h2>${currentEmployeeData.user_name}</h2>
                  <p>${currentEmployeeData.user_email} • ${capitalizeFirstLetter(currentEmployeeData.user_role)}</p>
                </div>
                <div>
                  <h2 style="color: #10b981;">$${currentEmployeeData.net_pay?.toFixed(2)}</h2>
                  <p>Net Pay</p>
                </div>
              </div>
              
              <div class="metrics">
                <div class="metric">
                  <div style="font-size: 24px; font-weight: bold; color: #3b82f6;">${currentEmployeeData.total_hours?.toFixed(2)}</div>
                  <div>Total Hours</div>
                </div>
                <div class="metric">
                  <div style="font-size: 24px; font-weight: bold; color: #10b981;">${currentEmployeeData.regular_hours?.toFixed(2)}</div>
                  <div>Regular Hours</div>
                </div>
                <div class="metric">
                  <div style="font-size: 24px; font-weight: bold; color: #f59e0b;">${currentEmployeeData.after_hours_hours?.toFixed(2)}</div>
                  <div>After Hours</div>
                </div>
                <div class="metric">
                  <div style="font-size: 24px; font-weight: bold; color: #8b5cf6;">$${currentEmployeeData.hourly_rate?.toFixed(2)}</div>
                  <div>Hourly Rate</div>
                </div>
              </div>
              
              <div class="breakdown">
                <div class="section">
                  <h3>Earnings</h3>
                  <div class="line-item">
                    <span>Regular Pay (${currentEmployeeData.regular_hours?.toFixed(2)}h × $${currentEmployeeData.hourly_rate?.toFixed(2)})</span>
                    <span>$${currentEmployeeData.regular_pay?.toFixed(2)}</span>
                  </div>
                  <div class="line-item">
                    <span>After Hours Pay (${currentEmployeeData.after_hours_hours?.toFixed(2)}h × $${currentEmployeeData.after_hours_rate?.toFixed(2)})</span>
                    <span>$${currentEmployeeData.after_hours_pay?.toFixed(2)}</span>
                  </div>
                  <div class="total">
                    <span>Gross Pay</span>
                    <span style="color: #10b981;">$${currentEmployeeData.gross_pay?.toFixed(2)}</span>
                  </div>
                </div>
                
                <div class="section">
                  <h3>Deductions</h3>
                  <div class="line-item">
                    <span>Social Security (6.2%)</span>
                    <span style="color: #ef4444;">-$${currentEmployeeData.social_security_deduction?.toFixed(2)}</span>
                  </div>
                  <div class="line-item">
                    <span>Medicare (1.45%)</span>
                    <span style="color: #ef4444;">-$${currentEmployeeData.medicare_deduction?.toFixed(2)}</span>
                  </div>
                  <div class="line-item">
                    <span>Federal Tax</span>
                    <span style="color: #ef4444;">-$${currentEmployeeData.federal_tax_deduction?.toFixed(2)}</span>
                  </div>
                  <div class="line-item">
                    <span>State Tax</span>
                    <span style="color: #ef4444;">-$${currentEmployeeData.state_tax_deduction?.toFixed(2)}</span>
                  </div>
                  <div class="total">
                    <span>Total Deductions</span>
                    <span style="color: #ef4444;">-$${currentEmployeeData.total_deductions?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <div class="final-pay">
                <h3>Final Net Pay</h3>
                <p>Amount to be paid to employee</p>
                <div style="font-size: 32px; font-weight: bold; color: #3b82f6;">$${currentEmployeeData.net_pay?.toFixed(2)}</div>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const handleBack = () => {
    if (from === 'ready-to-publish') {
      navigate('/ai-ready-to-publish');
    } else if (from === 'published') {
      navigate('/ai-published-posts');
    } else if (from === 'paystub-settings') {
      const tab = searchParams.get('tab') || 'paystubs';
      navigate(`/paystub-settings?activeTab=${tab}`);
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading paystub report...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Report not found'}</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const paystubData = post.paystub_data || {};
  const employeePaystubs = paystubData.employee_paystubs || [];
  const isIndividualView = !!employeeId;
  const currentEmployeeData = isIndividualView ? employeePaystubs.find(emp => emp.user_id === employeeId) : null;

  // Get selected employee data
  const getSelectedEmployeeData = () => {
    if (selectedEmployee === 'all') {
      return employeePaystubs;
    }
    return employeePaystubs.filter(emp => emp.user_id === selectedEmployee);
  };

  const selectedEmployeeData = getSelectedEmployeeData();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-medium text-gray-900">
              {isIndividualView && currentEmployeeData ? `${currentEmployeeData.user_name}'s Paystub` : 'Paystub Report'}
            </h1>
            
            <div className="flex items-center space-x-3">
              {isIndividualView && currentEmployeeData && (
                <button
                  onClick={handleDownloadPDF}
                  className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </button>
              )}
              
              <button
                onClick={handleBack}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            </div>
          </div>
          
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="h-4 w-4 mr-1" />
            Period: {formatDateOnly(paystubData.start_date)} to {formatDateOnly(paystubData.end_date)}
            {isIndividualView && currentEmployeeData && (
              <span className="ml-4 text-theme-blue">• {capitalizeFirstLetter(currentEmployeeData.user_role)}</span>
            )}
          </div>
        </div>

        {/* Content - Show individual employee view if accessed from employee profile */}
        {isIndividualView && currentEmployeeData ? (
          // Individual Employee Paystub View
          <div id="paystub-content" className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-theme-blue flex items-center justify-center text-white text-sm font-medium">
                    {currentEmployeeData.user_name.charAt(0)}
                  </div>
                  <div className="ml-3">
                    <h2 className="text-base font-semibold text-gray-900">{currentEmployeeData.user_name}</h2>
                    <p className="text-xs text-gray-600">{currentEmployeeData.user_email} • {capitalizeFirstLetter(currentEmployeeData.user_role)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-green-600">${currentEmployeeData.net_pay?.toFixed(2)}</div>
                  <div className="text-xs text-gray-500">Net Pay</div>
                </div>
              </div>
            </div>

            <div className="p-4">
              {/* Hours Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-theme-blue">{currentEmployeeData.total_hours?.toFixed(2)}</div>
                  <div className="text-xs text-gray-600">Total Hours</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-600">{currentEmployeeData.regular_hours?.toFixed(2)}</div>
                  <div className="text-xs text-gray-600">Regular Hours</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-lg font-bold text-orange-600">{currentEmployeeData.after_hours_hours?.toFixed(2)}</div>
                  <div className="text-xs text-gray-600">After Hours</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-lg font-bold text-purple-600">${currentEmployeeData.hourly_rate?.toFixed(2)}</div>
                  <div className="text-xs text-gray-600">Hourly Rate</div>
                </div>
              </div>

              {/* Pay Calculation Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Earnings */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Earnings</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-600">Regular Pay ({currentEmployeeData.regular_hours?.toFixed(2)}h × ${currentEmployeeData.hourly_rate?.toFixed(2)})</span>
                      <span className="text-sm font-medium">${currentEmployeeData.regular_pay?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-600">After Hours Pay ({currentEmployeeData.after_hours_hours?.toFixed(2)}h × ${currentEmployeeData.after_hours_rate?.toFixed(2)})</span>
                      <span className="text-sm font-medium">${currentEmployeeData.after_hours_pay?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-t-2 border-gray-200 font-semibold">
                      <span className="text-sm">Gross Pay</span>
                      <span className="text-green-600">${currentEmployeeData.gross_pay?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Deductions</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-600">Social Security (6.2%)</span>
                      <span className="text-sm font-medium text-red-600">-${currentEmployeeData.social_security_deduction?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-600">Medicare (1.45%)</span>
                      <span className="text-sm font-medium text-red-600">-${currentEmployeeData.medicare_deduction?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-600">Federal Tax</span>
                      <span className="text-sm font-medium text-red-600">-${currentEmployeeData.federal_tax_deduction?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-600">State Tax</span>
                      <span className="text-sm font-medium text-red-600">-${currentEmployeeData.state_tax_deduction?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-t-2 border-gray-200 font-semibold">
                      <span className="text-sm">Total Deductions</span>
                      <span className="text-red-600">-${currentEmployeeData.total_deductions?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Final Net Pay */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Final Net Pay</h3>
                    <p className="text-xs text-gray-600">Amount to be paid to employee</p>
                  </div>
                  <div className="text-2xl font-bold text-theme-blue">${currentEmployeeData.net_pay?.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-xl font-bold text-theme-blue">{paystubData.total_employees}</div>
                <div className="text-xs text-gray-600">Total Employees</div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-xl font-bold text-green-600">${paystubData.total_gross_pay?.toFixed(2)}</div>
                <div className="text-xs text-gray-600">Total Gross Pay</div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-xl font-bold text-red-600">${(paystubData.total_gross_pay - paystubData.total_net_pay)?.toFixed(2)}</div>
                <div className="text-xs text-gray-600">Total Deductions</div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-xl font-bold text-purple-600">${paystubData.total_net_pay?.toFixed(2)}</div>
                <div className="text-xs text-gray-600">Total Net Pay</div>
              </div>
            </div>

            {/* Employee Filter */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <label className="text-sm font-medium text-gray-700">View Employee:</label>
                </div>
                <div className="flex-1 max-w-xs">
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="all">All Employees Summary</option>
                    {employeePaystubs.map((employee) => (
                      <option key={employee.user_id} value={employee.user_id}>
                        {employee.user_name} - ${employee.net_pay?.toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedEmployee !== 'all' && (
                  <button
                    onClick={() => setSelectedEmployee('all')}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    Show All
                  </button>
                )}
              </div>
            </div>

            {/* Employee Details - Same as before */}
            {selectedEmployee === 'all' ? (
              // All Employees Summary
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">All Employees Summary</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Pay</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deductions</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Pay</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {employeePaystubs.map((employee) => (
                        <tr key={employee.user_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-6 w-6 rounded-full bg-theme-blue flex items-center justify-center text-white text-xs font-medium">
                                {employee.user_name.charAt(0)}
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">{employee.user_name}</div>
                                <div className="text-xs text-gray-500">{capitalizeFirstLetter(employee.user_role)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {employee.total_hours?.toFixed(2)}h
                            <div className="text-xs text-gray-500">
                              {employee.regular_hours?.toFixed(1)}h reg, {employee.after_hours_hours?.toFixed(1)}h after
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            ${employee.hourly_rate?.toFixed(2)}/hr
                            <div className="text-xs text-gray-500">
                              ${employee.after_hours_rate?.toFixed(2)}/hr after hours
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-green-600">
                            ${employee.gross_pay?.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600">
                            ${employee.total_deductions?.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-theme-blue">
                            ${employee.net_pay?.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      {/* Totals Row */}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">TOTALS</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {employeePaystubs.reduce((sum, emp) => sum + (emp.total_hours || 0), 0).toFixed(2)}h
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">-</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-green-600">
                          ${paystubData.total_gross_pay?.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600">
                          ${(paystubData.total_gross_pay - paystubData.total_net_pay)?.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-theme-blue">
                          ${paystubData.total_net_pay?.toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              // Individual Employee Details (from dropdown selection)
              selectedEmployeeData.map((employee) => (
                <div key={employee.user_id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-theme-blue flex items-center justify-center text-white text-sm font-medium">
                          {employee.user_name.charAt(0)}
                        </div>
                        <div className="ml-3">
                          <h2 className="text-base font-semibold text-gray-900">{employee.user_name}</h2>
                          <p className="text-xs text-gray-600">{employee.user_email} • {capitalizeFirstLetter(employee.user_role)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-green-600">${employee.net_pay?.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">Net Pay</div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    {/* Hours Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-lg font-bold text-theme-blue">{employee.total_hours?.toFixed(2)}</div>
                        <div className="text-xs text-gray-600">Total Hours</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-lg font-bold text-green-600">{employee.regular_hours?.toFixed(2)}</div>
                        <div className="text-xs text-gray-600">Regular Hours</div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 rounded-lg">
                        <div className="text-lg font-bold text-orange-600">{employee.after_hours_hours?.toFixed(2)}</div>
                        <div className="text-xs text-gray-600">After Hours</div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <div className="text-lg font-bold text-purple-600">${employee.hourly_rate?.toFixed(2)}</div>
                        <div className="text-xs text-gray-600">Hourly Rate</div>
                      </div>
                    </div>

                    {/* Pay Calculation Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Earnings */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Earnings</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center py-1 border-b border-gray-100">
                            <span className="text-xs text-gray-600">Regular Pay ({employee.regular_hours?.toFixed(2)}h × ${employee.hourly_rate?.toFixed(2)})</span>
                            <span className="text-sm font-medium">${employee.regular_pay?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center py-1 border-b border-gray-100">
                            <span className="text-xs text-gray-600">After Hours Pay ({employee.after_hours_hours?.toFixed(2)}h × ${employee.after_hours_rate?.toFixed(2)})</span>
                            <span className="text-sm font-medium">${employee.after_hours_pay?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center py-1 border-t-2 border-gray-200 font-semibold">
                            <span className="text-sm">Gross Pay</span>
                            <span className="text-green-600">${employee.gross_pay?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Deductions */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Deductions</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center py-1 border-b border-gray-100">
                            <span className="text-xs text-gray-600">Social Security (6.2%)</span>
                            <span className="text-sm font-medium text-red-600">-${employee.social_security_deduction?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center py-1 border-b border-gray-100">
                            <span className="text-xs text-gray-600">Medicare (1.45%)</span>
                            <span className="text-sm font-medium text-red-600">-${employee.medicare_deduction?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center py-1 border-b border-gray-100">
                            <span className="text-xs text-gray-600">Federal Tax</span>
                            <span className="text-sm font-medium text-red-600">-${employee.federal_tax_deduction?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center py-1 border-b border-gray-100">
                            <span className="text-xs text-gray-600">State Tax</span>
                            <span className="text-sm font-medium text-red-600">-${employee.state_tax_deduction?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center py-1 border-t-2 border-gray-200 font-semibold">
                            <span className="text-sm">Total Deductions</span>
                            <span className="text-red-600">-${employee.total_deductions?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Final Net Pay */}
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">Final Net Pay</h3>
                          <p className="text-xs text-gray-600">Amount to be paid to employee</p>
                        </div>
                        <div className="text-2xl font-bold text-theme-blue">${employee.net_pay?.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaystubReportView;